import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useOutletContext } from "react-router-dom";
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import {
  BROKER_FILTERED_ADMIN_COPY,
  brokerFilteredAdminProps,
} from '../../utils/brokerFilteredAdmin';
import { useInvitations, isUnfinishedInvitationSetup, unfinishedSetupPress } from '../../hooks/useInvitations';
import {
    unfinishedSetupActionLabel,
    unfinishedSetupBadgeLabel,
    unfinishedSetupConfirmCopy,
    unfinishedSetupFailureLabel,
    type UnfinishedSetupPress,
} from './unfinishedSetupCopy';
import { tierHasFeature, type SubscriptionTier } from '../../config/subscriptionTiers';
import { db, functions } from '../../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, query, where, orderBy, onSnapshot, getDocs } from 'firebase/firestore';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { Plus, UsersRound, EyeOff, Eye, FileBarChart, AlertTriangle, BookOpen, Inbox, Clock, CalendarDays } from 'lucide-react';
import { getAssignableRoles, canManageRole } from '@valutten/shared-utils/roles';
import PageHeader from '../../components/common/PageHeader';
import { HelpButton } from '../../components/help/HelpButton';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';

type AssignmentReason = 'broker_departure' | 'leave_of_absence' | 'restructure' | 'retirement' | 'other';

interface BrokerAssignment {
  id: string;
  sourceBrokerDocId: string;
  assignedToBrokerDocId: string;
  reason: AssignmentReason;
  notes?: string;
  status: 'active' | 'revoked';
  effectiveFrom: any;
  effectiveTo: any;
  assignedBy: string;
}

interface BrokerDoc {
  id: string;
  currentName: string;
  currentOfficeId?: string;
  status?: string;
  mergedInto?: string;
}

interface OutletContextType {
  userData: any;
  companyData: any;
  teamMembers?: any[];
  offices?: any[];
  userVisibleOffices?: any[];
  userVisibleTemplates?: any[];
  loanBooks?: ResolvedLoanBook[];
}

const REPORT_OPTIONS = [
  { slug: 'settlement-age', label: 'Settlement Age' },
  { slug: 'clawbacks', label: 'Clawbacks' },
  { slug: 'arrears', label: 'Arrears' },
  { slug: 'discharged-loans', label: 'Discharged Loans' },
  { slug: 'loans', label: 'Loan Portfolio' },
  { slug: 'trail-drop-off', label: 'Loan Trail Drop-Off' },
  { slug: 'book-health', label: 'Book Health' },
  { slug: 'customer-portfolio', label: 'Customer Portfolio' },
  { slug: 'refinance-intelligence', label: 'Client Retention' },
  { slug: 'acquired-books', label: 'Acquired Books' },
  { slug: 'commission-settings', label: 'Commission Settings' },
  { slug: 'book-trend', label: 'Book Trend Report' },
] as const;

// Lead Capture page visibility options for broker-family roles. Lead Inbox +
// My Availability are baseline (always allowed) and shown as disabled+checked
// in the UI for clarity. Admin-tier roles bypass this filter entirely (they
// always see the full nav). Slugs MUST match the gated slugs declared in
// LeadCaptureTopNav.tsx so the filter and the saved data agree.
const LEAD_PAGE_OPTIONS = [
  { slug: 'how_it_works',     label: 'How It Works',      defaultOn: true,  baseline: false },
  { slug: 'attribution',      label: 'Attribution / Reports', defaultOn: false, baseline: false },
  { slug: 'email_automation', label: 'Email Automation',  defaultOn: false, baseline: false },
  { slug: 'templates',        label: 'Templates',         defaultOn: false, baseline: false },
] as const;
type LeadPageSlug = (typeof LEAD_PAGE_OPTIONS)[number]['slug'];

// Roles for which the availability override section should appear in the modal.
const AVAILABILITY_OVERRIDE_ROLES = new Set(['broker', 'broker_assistant', 'credit_analyst']);

// Day-of-week labels for the read-only weekly schedule preview.
const WEEKDAY_LABELS: Array<{ key: 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'; label: string }> = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

/**
 * Shown when a revoke landed but one of its legs did not. It deliberately does NOT
 * say "revoke again": the row's only action at this point is Restore, because the
 * page derives the action by toggling the current status, so an operator following
 * a retry instruction would RE-GRANT the account.
 */
function revokeWarning(result: {
  sessionEnded?: boolean;
  claimsCleared?: boolean;
  invitationsSwept?: boolean;
  revokeRaced?: boolean;
} | undefined) {
  if (result?.claimsCleared === false) {
    // The leg that matters did not run. Saying "claims cleared" here would be the
    // control reporting completion it did not achieve, on the one leg that decides
    // whether the account is still authorised.
    return (
      'Status set to revoked, but CLEARING THEIR ACCESS FAILED. The account may still ' +
      'be authorised. Revoke again, and if it fails again escalate: this is not a ' +
      'completed revoke.'
    );
  }
  if (result?.revokeRaced === true) {
    return (
      'Status was changed back to active while the revoke was running, so this account ' +
      'may hold live access again. Check it before treating the revoke as done.'
    );
  }
  if (result?.invitationsSwept === false) {
    return (
      'Access revoked and their claims cleared, but any invitation still outstanding for ' +
      'this person could NOT be cancelled. An unaccepted invite lets them restore their ' +
      'own access. Check Invitations for this person before treating the revoke as complete.'
    );
  }
  return (
    'Access revoked and their claims cleared, so the account is authorised nowhere. Ending ' +
    'their existing sign-in session did not complete; that session expires within the hour ' +
    'on its own, and the daily access sweep reports it if it persists.'
  );
}

const TeamManagementPage = () => {
  const { userData, companyData, teamMembers, offices, userVisibleOffices, loanBooks = [] } = useOutletContext<OutletContextType>();
    const { user, claims, isBrokerFilteredAdmin, updateUserRole, updateUserStatus } = useAuth();
    const { invitations, unfinishedInvitations, loading: invitationsLoading, error: invitationsError } = useInvitations(userData?.companyId);

    // WS8b M2 (V5-02): an `accepted` invitation whose finalisation never
    // finished is shown alongside the in-progress ones. It is the only
    // administrator-visible handle on a setup that granted access but left the
    // bootstrap session un-revoked; before this it was invisible here and
    // refused by cancelInvitation, so neither party had a route out.
    const invitationRows = useMemo(
        () => [...invitations, ...unfinishedInvitations],
        [invitations, unfinishedInvitations],
    );
    const [error, setError] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('broker');
    // Broker family includes credit_analyst — they get the same access controls
    // (loan books, hide commissions, visible payment types, allowed reports).
    const isBrokerFamilyRole = inviteRole === 'broker' || inviteRole === 'broker_assistant' || inviteRole === 'credit_analyst';
    // credit_analyst supports an extra control (linked broker docs) restricted to
    // super_admin and administrator. office_admin cannot configure analyst linkage.
    const canConfigureAnalystLinkage = userData?.role === 'super_admin' || userData?.role === 'administrator' || claims?.isFounder;
    // A broker-restricted office_admin is default-denied at the server
    // chokepoint for every mutation on this page, so its write controls are
    // disabled and labelled instead of failing on press (F6). Gated here as
    // well as on the V2 page because the two are selected by a DIFFERENT flag
    // (`team_management_v2`) than the one that can mint a filtered admin
    // (`office_admin_access_controls`): a tenant carrying only the latter would
    // otherwise land her on this page with every control live.
    const callerIsBrokerFiltered = isBrokerFilteredAdmin;
    const [inviteLinkedBrokerDocIds, setInviteLinkedBrokerDocIds] = useState<string[]>([]);
    const [inviteAccessScope, setInviteAccessScope] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inviteMessage, setInviteMessage] = useState({ type: '', text: '' });
    const [pending, setPending] = useState<any>({});
    const [assignAllOffices, setAssignAllOffices] = useState(false);
    const [pendingRoleChange, setPendingRoleChange] = useState<any>({});
    const [inviteExpectedName, setInviteExpectedName] = useState('');
    // PR 1A-v3.1 (2026-05-14): explicit first name + surname inputs for
    // credit_analyst — operational-only records have no email and no
    // broker auto-fill, so the admin needs a clear name surface to
    // recognise the analyst record on the broker commission settings page.
    const [inviteFirstName, setInviteFirstName] = useState('');
    const [inviteSurname, setInviteSurname] = useState('');
    const [inviteNotes, setInviteNotes] = useState('');
    const [selectedBrokerId, setSelectedBrokerId] = useState('');
    // Adopt-existing linkage: the operational analyst record this invitation
    // should claim on accept. Explicit selection only, never inferred from
    // name or email.
    const [selectedAnalystId, setSelectedAnalystId] = useState('');
    const [inviteHideCommissions, setInviteHideCommissions] = useState(true);
    // Loan-value visibility for hidden-commission invitees (2026-07-10 policy):
    // loan amounts show in report tables by default; this hides them too.
    const [inviteHideLoanAmounts, setInviteHideLoanAmounts] = useState(false);

    // Granular access control state (broker role only)
    const [reportAccessMode, setReportAccessMode] = useState<'all' | 'custom'>('custom');
    const [selectedReports, setSelectedReports] = useState<string[]>([]);
    const [templateAccessMode, setTemplateAccessMode] = useState<'all' | 'custom'>('custom');
    // Stores loan_book IDs (subcollection doc IDs for unified-mode, fileTemplate IDs for legacy).
    // Sent to backend as `allowedLoanBookIds`; backend derives allowedTemplateIds + allowedEntityCodes.
    const [selectedLoanBookIds, setSelectedLoanBookIds] = useState<string[]>([]);

    // Tab state
    const [activeTab, setActiveTab] = useState<'invitations' | 'users' | 'broker_approvals' | 'broker_assignments' | 'operational_roles'>('invitations');

    // Operational Roles tab state — credit-analyst commission-recipient records.
    const [operationalAnalysts, setOperationalAnalysts] = useState<
      Array<{ analystId: string; displayName: string; email: string | null; status: 'active' | 'archived'; hasLogin: boolean; notes: string | null }>
    >([]);
    const [operationalLoading, setOperationalLoading] = useState(false);
    // PR 1A-v3 (2026-05-13): the standalone "Add Credit Analyst" form on the
    // Operational Roles tab was retired — Invite User (with email blank) is
    // now the single entry point so admins don't have two parallel UX paths.
    // These state slots are retained as no-op aliases to avoid breaking any
    // residual references in archive/restore flows; setters are still wired
    // to the existing handlers below.
    // PR 1A-v2: Operational Roles form is identity-only (name, email).
    // paymentMode + performance target fields removed from this form — they
    // now live PER-BROKER on the broker commission settings page (Credit
    // Analysts section), so the same analyst can have different settings
    // per broker. Mirrors how brokers configure their own % + target.
    // analystFormSubmitting retired with the Operational Roles inline form (PR 1A-v3).
    const canManageOperationalRoles =
      userData?.role === 'super_admin' || userData?.role === 'administrator' || claims?.isFounder;

    // Broker assignment state
    const canManageAssignments = ['super_admin', 'office_admin'].includes(userData?.role);
    const [assignments, setAssignments] = useState<BrokerAssignment[]>([]);
    const [assignmentsLoading, setAssignmentsLoading] = useState(true);
    const [brokerDocs, setBrokerDocs] = useState<BrokerDoc[]>([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignSource, setAssignSource] = useState('');
    const [assignTarget, setAssignTarget] = useState('');
    const [assignReason, setAssignReason] = useState<AssignmentReason>('broker_departure');
    const [assignNotes, setAssignNotes] = useState('');
    const [assignSubmitting, setAssignSubmitting] = useState(false);
    const [assignError, setAssignError] = useState('');
    const [revokingAssignment, setRevokingAssignment] = useState<Record<string, boolean>>({});
    const [togglingVisibility, setTogglingVisibility] = useState<Record<string, boolean>>({});

    // Edit permissions modal state
    const [editPermissionsUser, setEditPermissionsUser] = useState<any>(null);
    // credit_analyst-only: the brokers this analyst is linked to (view/portfolio scope).
    // super_admin/administrator only. Editing here re-points the link without re-inviting.
    const [editLinkedBrokerDocIds, setEditLinkedBrokerDocIds] = useState<string[]>([]);
    // Per-(broker, loan book) view grants: each entry links this user to ONE loan book of ONE broker.
    const [editLinkedLoanBookGrants, setEditLinkedLoanBookGrants] = useState<Array<{ brokerDocId: string; loanBookId: string }>>([]);
    // Owner-operator "also a broker" identity link (administrator/super_admin targets).
    const [editBrokerIdentityDocId, setEditBrokerIdentityDocId] = useState<string>('');
    const [savingBrokerIdentity, setSavingBrokerIdentity] = useState(false);
    const [editReportAccessMode, setEditReportAccessMode] = useState<'all' | 'custom'>('all');
    const [editSelectedReports, setEditSelectedReports] = useState<string[]>([]);
    const [editTemplateAccessMode, setEditTemplateAccessMode] = useState<'all' | 'custom'>('all');
    const [editSelectedLoanBookIds, setEditSelectedLoanBookIds] = useState<string[]>([]);
    const [savingPermissions, setSavingPermissions] = useState(false);

    // Lead Capture admin-config state (rendered in the same Edit Permissions modal).
    // - allowedLeadPages: nav-tab visibility filter for broker-family targets.
    // - availability override fields: write through to the broker's availability subdoc.
    const [editAllowedLeadPages, setEditAllowedLeadPages] = useState<LeadPageSlug[]>([]);
    const [editAcceptingNewLeads, setEditAcceptingNewLeads] = useState<boolean>(true);
    const [editMaxOpenLeads, setEditMaxOpenLeads] = useState<number | undefined>(undefined);
    const [editLeadOutOfOfficeEnabled, setEditLeadOutOfOfficeEnabled] = useState<boolean>(false);
    const [editLeadOutOfOfficeUntil, setEditLeadOutOfOfficeUntil] = useState<string>('');
    // Read-only snapshot of the broker's weekly schedule (admin doesn't edit it
    // here — they're directed to /leads/availability for deep edits).
    const [editLeadWeeklySchedule, setEditLeadWeeklySchedule] = useState<Record<string, Array<{ start: string; end: string }>> | null>(null);
    // Track whether availability fields were touched by the admin so we only send
    // them on save when the admin actually changed something (avoids spurious
    // writes that would clobber broker-side edits made between modal-open + save).
    const [editLeadFieldsDirty, setEditLeadFieldsDirty] = useState<{
      accepting: boolean;
      maxOpen: boolean;
      ooo: boolean;
      pages: boolean;
    }>({ accepting: false, maxOpen: false, ooo: false, pages: false });

    // Broker approvals state
    const [brokerApprovals, setBrokerApprovals] = useState<any[]>([]);
    const [approvalsLoading, setApprovalsLoading] = useState(true);
    const [approvalsError, setApprovalsError] = useState('');
    const [processingApproval, setProcessingApproval] = useState<any>({});

    // Cancel invitation state
    const [cancellingInvitation, setCancellingInvitation] = useState<any>({});

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        confirmLabel?: string;
        variant?: 'danger' | 'primary';
        onConfirm: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
    });

    // NOTE: Broker-to-commission data linking happens automatically during
    // invitation acceptance based on user's display name, not during invitation.
    // This maintains separation of concerns: Team Management = access control only.

    // Role hierarchy: filter invite dropdown and disable controls for higher-ranked users.
    // credit_analyst is universally available (broker-family). marketing is gated by the
    // lead_capture entitlement using the SAME client-side derivation MorphingSidebar uses —
    // companyData.featureFlags.lead_capture override OR tier-default via FEATURE_REGISTRY
    // (lead_capture.requiredTier='enterprise'). Backend createInvitation has its own
    // requireFeature(LEAD_CAPTURE) gate as defense-in-depth.
    const subscriptionTier = useMemo((): SubscriptionTier => {
      const rawTier = (companyData?.subscription?.tier as string | undefined)?.toLowerCase() || '';
      if (rawTier === 'pro' || rawTier === 'professional') return 'pro';
      if (rawTier === 'enterprise' || rawTier === 'business') return 'enterprise';
      return 'plus';
    }, [companyData?.subscription?.tier]);
    const hasLeadCapture =
      companyData?.featureFlags?.lead_capture === true ||
      tierHasFeature(subscriptionTier, 'lead_capture');
    const assignableRoles = useMemo(() => {
      const roles = getAssignableRoles(userData?.role || '', !!claims?.isFounder);
      if (hasLeadCapture) return roles;
      return roles.filter((r) => r !== 'marketing');
    }, [userData?.role, claims?.isFounder, hasLeadCapture]);

    // Office-scoped team visibility for office_admin
    const visibleMembers = useMemo(() => {
      if (!teamMembers) return [];
      if (userData?.role === 'office_admin' && !userData?.canSeeAllOffices) {
        const myOffices = new Set(userData?.assignedOffices || []);
        return teamMembers.filter((m: any) => {
          // Never show super_admin or founder to office_admin
          if (m.role === 'super_admin' || m.isFounder) return false;
          // Show users who share at least one office
          return m.canSeeAllOffices ||
            (m.assignedOffices || []).some((id: string) => myOffices.has(id));
        });
      }
      return teamMembers;
    }, [teamMembers, userData]);

    // Offices available for invite modal
    const availableOffices = userVisibleOffices || offices || [];

    // Fetch broker approvals
    useEffect(() => {
        if (!userData?.companyId) return;

        setApprovalsLoading(true);
        setApprovalsError('');

        const q = query(
            collection(db, 'broker_link_approvals'),
            where('companyId', '==', userData.companyId),
            where('status', '==', 'pending'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const approvals = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setBrokerApprovals(approvals);
                setApprovalsLoading(false);
            },
            (err) => {
                console.error('Error fetching broker approvals:', err);
                setApprovalsError('Failed to load broker approvals: ' + err.message);
                setApprovalsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [userData?.companyId]);

    // Fetch broker docs (one-time) — used by invite dropdown AND assignment tab
    useEffect(() => {
        if (!userData?.companyId) return;
        const fetchBrokerDocs = async () => {
            try {
                const brokersSnap = await getDocs(collection(db, `companies/${userData.companyId}/brokers`));
                const docs = brokersSnap.docs.map(doc => ({
                    id: doc.id,
                    currentName: doc.data().currentName || doc.id,
                    currentOfficeId: doc.data().currentOfficeId,
                    status: doc.data().status,
                    mergedInto: doc.data().mergedInto,
                } as BrokerDoc));
                setBrokerDocs(docs);
            } catch (err) {
                console.error('Error fetching broker docs:', err);
            }
        };
        fetchBrokerDocs();
    }, [userData?.companyId]);

    // Fetch broker assignments (real-time)
    useEffect(() => {
        if (!userData?.companyId || !canManageAssignments) {
            setAssignmentsLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Real-time listener for broker_assignments
        const q = query(
            collection(db, `companies/${companyId}/broker_assignments`),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BrokerAssignment));
                setAssignments(docs);
                setAssignmentsLoading(false);
            },
            (err) => {
                console.error('Error fetching broker assignments:', err);
                setAssignmentsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [userData?.companyId, canManageAssignments]);

    // Helper: resolve broker doc ID to display name
    const getBrokerName = (brokerDocId: string): string => {
        const doc = brokerDocs.find(b => b.id === brokerDocId);
        return doc?.currentName || brokerDocId;
    };

    // Lazy-load credit-analyst records when Operational Roles tab is opened.
    const refreshOperationalAnalysts = useCallback(async () => {
        if (!userData?.companyId) return;
        setOperationalLoading(true);
        try {
            const fn = httpsCallable<
              { companyId: string; includeArchived?: boolean },
              { records: typeof operationalAnalysts }
            >(functions, 'listCreditAnalystRecords');
            const result = await fn({ companyId: userData.companyId, includeArchived: true });
            setOperationalAnalysts(result.data.records ?? []);
        } catch (err: any) {
            console.error('[TeamManagement] listCreditAnalystRecords failed:', err);
            toast.error('Failed to load credit analysts: ' + (err.message || 'Unknown error'));
        } finally {
            setOperationalLoading(false);
        }
    }, [userData?.companyId]);

    // Eager-load on page mount so the tab badge shows the correct count
    // immediately. Cost: one extra Firestore read per Team Management page
    // load (negligible). Without this, the badge reads (0) until the admin
    // clicks the tab, which is a stale-data bug for tenants with existing
    // analyst records (PR #708 follow-up).
    useEffect(() => {
        if (userData?.companyId && canManageOperationalRoles) {
            refreshOperationalAnalysts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData?.companyId, canManageOperationalRoles]);

    // handleCreateAnalystRecord retired with the Operational Roles inline form
    // (PR 1A-v3, 2026-05-13). Operational-only analyst creation now flows
    // through handleInviteSubmit when the credit_analyst role is selected
    // with an empty email — same backend callable (manageCreditAnalystRecord)
    // but invoked from the single Invite User modal entry point.

    const handleArchiveAnalystRecord = async (analystId: string, currentStatus: 'active' | 'archived') => {
        if (!userData?.companyId) return;
        try {
            const fn = httpsCallable(functions, 'manageCreditAnalystRecord');
            await fn({
                action: currentStatus === 'active' ? 'archive' : 'restore',
                companyId: userData.companyId,
                analystId,
            });
            toast.success(currentStatus === 'active' ? 'Analyst archived' : 'Analyst restored');
            await refreshOperationalAnalysts();
        } catch (err: any) {
            console.error('[TeamManagement] manageCreditAnalystRecord archive failed:', err);
            toast.error('Failed: ' + (err.message || 'Unknown error'));
        }
    };

    // Adoptable analyst records for the invite modal: active, and without a
    // login. A record that already has a login belongs to somebody who can sign
    // in, so it is not an adoption target.
    const adoptableAnalysts = useMemo(() => {
        return operationalAnalysts
            .filter((a) => a.status === 'active' && !a.hasLogin)
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [operationalAnalysts]);

    // Brokers available for assignment (exclude those with active source assignments)
    const activeSources = useMemo(() => {
        return new Set(assignments.filter(a => a.status === 'active').map(a => a.sourceBrokerDocId));
    }, [assignments]);

    const handleCreateAssignment = async () => {
        if (!assignSource || !assignTarget || !assignReason) return;
        setAssignSubmitting(true);
        setAssignError('');
        try {
            const fn = httpsCallable(functions, 'assignBrokerLoans');
            await fn({
                companyId: userData.companyId,
                sourceBrokerDocId: assignSource,
                assignedToBrokerDocId: assignTarget,
                reason: assignReason,
                notes: assignNotes || undefined,
            });
            setShowAssignModal(false);
            setAssignSource('');
            setAssignTarget('');
            setAssignReason('broker_departure');
            setAssignNotes('');
        } catch (err: any) {
            setAssignError(err.message || 'Failed to create assignment');
        } finally {
            setAssignSubmitting(false);
        }
    };

    const handleRevokeAssignment = (assignmentId: string, sourceName: string) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Revoke Loan Assignment',
            message: `Are you sure you want to revoke the loan assignment from ${sourceName}? The assigned broker will no longer see the source broker's loans.`,
            confirmLabel: 'Revoke Assignment',
            variant: 'danger',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setRevokingAssignment(prev => ({ ...prev, [assignmentId]: true }));
                try {
                    const fn = httpsCallable(functions, 'revokeBrokerLoanAssignment');
                    await fn({ companyId: userData.companyId, assignmentId });
                } catch (err: any) {
                    toast.error('Failed to revoke assignment: ' + (err.message || 'Unknown error'));
                } finally {
                    setRevokingAssignment(prev => ({ ...prev, [assignmentId]: false }));
                }
            },
        });
    };

    const handleToggleCommissionVisibility = async (userId: string, currentValue: boolean) => {
        setTogglingVisibility(prev => ({ ...prev, [userId]: true }));
        try {
            const fn = httpsCallable(functions, 'toggleCommissionVisibility');
            await fn({
                companyId: userData.companyId,
                userId,
                hideCommissions: !currentValue,
            });
        } catch (err: any) {
            toast.error('Failed to toggle commission visibility: ' + (err.message || 'Unknown error'));
        } finally {
            setTogglingVisibility(prev => ({ ...prev, [userId]: false }));
        }
    };

    // Loan-value visibility for a hidden-commission user (2026-07-10 policy):
    // loan amounts show in report tables by default; this hides them too.
    const handleToggleLoanAmountVisibility = async (userId: string, currentValue: boolean) => {
        setTogglingVisibility(prev => ({ ...prev, [userId]: true }));
        try {
            const fn = httpsCallable(functions, 'toggleCommissionVisibility');
            await fn({
                companyId: userData.companyId,
                userId,
                hideLoanAmounts: !currentValue,
            });
        } catch (err: any) {
            toast.error('Failed to toggle loan amount visibility: ' + (err.message || 'Unknown error'));
        } finally {
            setTogglingVisibility(prev => ({ ...prev, [userId]: false }));
        }
    };

    const openEditPermissions = async (member: any) => {
        setEditPermissionsUser(member);
        const hasReports = Array.isArray(member.allowedReports) && member.allowedReports.length > 0;
        // Prefer new allowedLoanBookIds; fall back to legacy allowedTemplateIds (for Entourage
        // the two IDs are equivalent because legacy loan_book_id === fileTemplate_id).
        const storedLoanBookIds: string[] | undefined = Array.isArray(member.allowedLoanBookIds) && member.allowedLoanBookIds.length > 0
            ? member.allowedLoanBookIds
            : (Array.isArray(member.allowedTemplateIds) && member.allowedTemplateIds.length > 0 ? member.allowedTemplateIds : undefined);
        const hasRestriction = Boolean(storedLoanBookIds && storedLoanBookIds.length > 0);
        setEditReportAccessMode(hasReports ? 'custom' : 'all');
        setEditSelectedReports(hasReports ? member.allowedReports : []);
        setEditTemplateAccessMode(hasRestriction ? 'custom' : 'all');
        setEditSelectedLoanBookIds(hasRestriction ? (storedLoanBookIds as string[]) : []);

        // credit_analyst broker linkage — prefill from the user doc. Preserves links
        // to merged/inactive brokers (resolver follows merge chains) even though the
        // checkbox list only renders active brokers.
        setEditLinkedBrokerDocIds(Array.isArray(member.linkedBrokerDocIds) ? member.linkedBrokerDocIds : []);
        setEditLinkedLoanBookGrants(Array.isArray(member.linkedLoanBookGrants) ? member.linkedLoanBookGrants : []);

        // "Also a broker" owner-operator link — prefill from the user doc's brokerDocId
        // (only meaningful for administrator/super_admin targets; the section is gated below).
        setEditBrokerIdentityDocId(member.brokerDocId ?? '');

        // Lead Capture state — populate from user doc (allowedLeadPages) + the
        // broker's availability subdoc (acceptingNewLeads / maxOpenLeads / OOO /
        // weekly schedule preview). All fields default to "broker default" when
        // missing so the admin sees the broker's current state, not blanks.
        setEditLeadFieldsDirty({ accepting: false, maxOpen: false, ooo: false, pages: false });
        const storedLeadPages: string[] = Array.isArray(member.allowedLeadPages)
            ? (member.allowedLeadPages as string[]).filter((s) => LEAD_PAGE_OPTIONS.some((o) => o.slug === s))
            : LEAD_PAGE_OPTIONS.filter((o) => o.defaultOn).map((o) => o.slug);
        setEditAllowedLeadPages(storedLeadPages as LeadPageSlug[]);

        // Defaults for availability — used if the broker has no availability doc yet.
        setEditAcceptingNewLeads(true);
        setEditMaxOpenLeads(undefined);
        setEditLeadOutOfOfficeEnabled(false);
        setEditLeadOutOfOfficeUntil('');
        setEditLeadWeeklySchedule(null);

        // Only fetch the availability subdoc for broker-family targets — admins/
        // marketing don't have routing-eligibility state.
        if (AVAILABILITY_OVERRIDE_ROLES.has(member.role) && userData?.companyId && member.id) {
            try {
                const availSnap = await getDoc(doc(db, `companies/${userData.companyId}/brokers/${member.id}/availability/default`));
                if (availSnap.exists()) {
                    const d = availSnap.data() as any;
                    if (typeof d.acceptingNewLeads === 'boolean') setEditAcceptingNewLeads(d.acceptingNewLeads);
                    if (typeof d.maxOpenLeads === 'number') setEditMaxOpenLeads(d.maxOpenLeads);
                    if (d.outOfOffice && typeof d.outOfOffice === 'object') {
                        setEditLeadOutOfOfficeEnabled(!!d.outOfOffice.enabled);
                        // Firestore Timestamp -> YYYY-MM-DD for the date input.
                        const until = d.outOfOffice.until;
                        if (until && typeof until.toDate === 'function') {
                            const dt = until.toDate();
                            const yyyy = dt.getFullYear();
                            const mm = String(dt.getMonth() + 1).padStart(2, '0');
                            const dd = String(dt.getDate()).padStart(2, '0');
                            setEditLeadOutOfOfficeUntil(`${yyyy}-${mm}-${dd}`);
                        }
                    }
                    if (d.weeklySchedule && typeof d.weeklySchedule === 'object') {
                        setEditLeadWeeklySchedule(d.weeklySchedule);
                    }
                }
            } catch (err) {
                // Non-fatal — modal still opens with defaults; admin can still
                // override fields, save will create the doc.
                console.warn('[openEditPermissions] failed to load availability:', err);
            }
        }
    };

    const handleSavePermissions = async () => {
        if (!editPermissionsUser) return;
        setSavingPermissions(true);
        try {
            const payload: Record<string, any> = {
                companyId: userData.companyId,
                userId: editPermissionsUser.id,
                allowedReports: editReportAccessMode === 'custom' && editSelectedReports.length > 0
                    ? editSelectedReports : undefined,
                allowedLoanBookIds: editTemplateAccessMode === 'custom' && editSelectedLoanBookIds.length > 0
                    ? editSelectedLoanBookIds : undefined,
            };

            // credit_analyst broker linkage — super_admin/administrator only. Always send
            // the current selection so an empty array clears the links. The backend
            // rejects this field for non-broker-family roles, so only include it here.
            if ((editPermissionsUser.role === 'broker' || editPermissionsUser.role === 'broker_assistant' || editPermissionsUser.role === 'credit_analyst') && canConfigureAnalystLinkage) {
                payload.linkedBrokerDocIds = editLinkedBrokerDocIds;
                // Per-(broker, loan book) grants: send only complete pairs; empty array clears them.
                payload.linkedLoanBookGrants = editLinkedLoanBookGrants.filter((g) => g.brokerDocId && g.loanBookId);
            }

            // Lead Capture admin overrides — only include fields the admin actually
            // touched. Sending undefined fields is a no-op on the backend; sending
            // an empty array clears the user-doc field. The backend treats absence
            // of availability fields as "do not touch the availability subdoc",
            // which prevents clobbering broker-side concurrent edits.
            const isBrokerFamilyTarget = AVAILABILITY_OVERRIDE_ROLES.has(editPermissionsUser.role);
            if (hasLeadCapture && editLeadFieldsDirty.pages) {
                payload.allowedLeadPages = editAllowedLeadPages;
            }
            if (isBrokerFamilyTarget) {
                if (editLeadFieldsDirty.accepting) {
                    payload.acceptingNewLeads = editAcceptingNewLeads;
                }
                if (editLeadFieldsDirty.maxOpen) {
                    // 0 → null (clear cap). undefined input also normalises to null.
                    payload.maxOpenLeads = editMaxOpenLeads === undefined || editMaxOpenLeads === 0
                        ? null
                        : editMaxOpenLeads;
                }
                if (editLeadFieldsDirty.ooo) {
                    payload.outOfOffice = {
                        enabled: editLeadOutOfOfficeEnabled,
                        // Empty string → null (clears the date). YYYY-MM-DD from the
                        // <input type="date"> is parsed as local-midnight which the
                        // backend re-serialises into a Firestore Timestamp.
                        until: editLeadOutOfOfficeUntil
                            ? new Date(editLeadOutOfOfficeUntil).toISOString()
                            : null,
                    };
                }
            }

            const fn = httpsCallable(functions, 'updateBrokerAccessPermissions');
            await fn(payload);
            toast.success('Permissions updated successfully');
            setEditPermissionsUser(null);
        } catch (err: any) {
            toast.error('Failed to update permissions: ' + (err.message || 'Unknown error'));
        } finally {
            setSavingPermissions(false);
        }
    };

    // "Also a broker" — link/unlink an administrator/super_admin owner-operator to
    // their broker doc so the Client Care digests (discharge/arrears/clawback +
    // weekly call list + reminders) route to them. Separate from Save Permissions:
    // it has its own validation (one broker doc → one recipient) and feedback.
    const handleSaveBrokerIdentity = async () => {
        if (!editPermissionsUser) return;
        setSavingBrokerIdentity(true);
        try {
            const fn = httpsCallable(functions, 'linkUserAsBroker');
            await fn({
                companyId: userData.companyId,
                userId: editPermissionsUser.id,
                brokerDocId: editBrokerIdentityDocId || null,
            });
            toast.success(editBrokerIdentityDocId ? 'Broker link saved' : 'Broker link removed');
            setEditPermissionsUser(null);
        } catch (err: any) {
            toast.error('Failed to update broker link: ' + (err.message || 'Unknown error'));
        } finally {
            setSavingBrokerIdentity(false);
        }
    };

    // WS8b M5 (V6-03): `press` is null for an ordinary in-progress invitation
    // (the base Cancel), 'end_session' for the first press on an unfinished
    // setup and 'dismiss' for the second. All three go through the SAME
    // callable; the server decides from its own transactional read, so a stale
    // row cannot make the client ask for the wrong action.
    const handleCancelInvitation = (
        invitationId: string,
        email: string,
        press: UnfinishedSetupPress | null = null,
    ) => {
        // WS8b M2 (V5-02): the copy must not claim to un-grant. This invitation
        // was already accepted, so ending the setup session does NOT remove the
        // access it granted; saying otherwise would leave an administrator
        // believing they had revoked someone.
        const unfinishedCopy = press ? unfinishedSetupConfirmCopy(email, press) : null;
        setConfirmDialog({
            isOpen: true,
            title: unfinishedCopy ? unfinishedCopy.title : 'Cancel Invitation',
            message: unfinishedCopy
                ? unfinishedCopy.message
                : `Are you sure you want to cancel the invitation for ${email}?`,
            confirmLabel: unfinishedCopy ? unfinishedCopy.confirmLabel : 'Cancel Invitation',
            variant: 'danger',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setCancellingInvitation((prev: any) => ({ ...prev, [invitationId]: true }));
                try {
                    const cancelInvitation = httpsCallable(functions, 'cancelInvitation');
                    await cancelInvitation({ invitationId });
                } catch (err: any) {
                    console.error('Failed to cancel invitation:', err);
                    toast.error(
                        (press ? unfinishedSetupFailureLabel(press) : 'Failed to cancel invitation: ')
                        + (err.message || 'Unknown error'),
                    );
                } finally {
                    setCancellingInvitation((prev: any) => ({ ...prev, [invitationId]: false }));
                }
            }
        });
    };

    const handleApproveBroker = (approvalId: string, brokerName: string) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Approve Broker Link',
            message: `Approve broker link for ${brokerName}?`,
            confirmLabel: 'Approve',
            variant: 'primary',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setProcessingApproval((prev: any) => ({ ...prev, [approvalId]: true }));
                try {
                    const approveBrokerLink = httpsCallable(functions, 'approveBrokerLink');
                    await approveBrokerLink({ approvalId, approved: true });
                } catch (err: any) {
                    console.error('Failed to approve broker:', err);
                    toast.error('Failed to approve broker: ' + (err.message || 'Unknown error'));
                } finally {
                    setProcessingApproval((prev: any) => ({ ...prev, [approvalId]: false }));
                }
            }
        });
    };

    const handleRejectBroker = (approvalId: string, brokerName: string) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Reject Broker Link',
            message: `Are you sure you want to reject the broker link for ${brokerName}?`,
            confirmLabel: 'Reject',
            variant: 'danger',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setProcessingApproval((prev: any) => ({ ...prev, [approvalId]: true }));
                try {
                    const approveBrokerLink = httpsCallable(functions, 'approveBrokerLink');
                    await approveBrokerLink({
                        approvalId,
                        approved: false,
                        rejectionReason: 'Rejected by administrator'
                    });
                } catch (err: any) {
                    console.error('Failed to reject broker:', err);
                    toast.error('Failed to reject broker: ' + (err.message || 'Unknown error'));
                } finally {
                    setProcessingApproval((prev: any) => ({ ...prev, [approvalId]: false }));
                }
            }
        });
    };

    const handleInviteSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setInviteMessage({ type: '', text: '' });
        if ((inviteRole === 'office_admin' || inviteRole === 'administrator') && !assignAllOffices && inviteAccessScope.length === 0) {
            setInviteMessage({ type: 'error', text: 'Please select at least one office or choose "Assign All Offices".' });
            setIsSubmitting(false);
            return;
        }
        if (inviteRole === 'marketing' && inviteAccessScope.length === 0) {
            setInviteMessage({ type: 'error', text: 'Please select at least one office for the marketing user.' });
            setIsSubmitting(false);
            return;
        }
        // broker + broker_assistant require an explicit broker selection (existing UX).
        // credit_analyst is optional: when absent, acceptInvitation can match by display
        // name OR the analyst can be linked retrospectively via Edit Permissions.
        if ((inviteRole === 'broker' || inviteRole === 'broker_assistant') && !selectedBrokerId) {
            setInviteMessage({ type: 'error', text: 'Please select a broker from the dropdown.' });
            setIsSubmitting(false);
            return;
        }
        // PR 1A-v3.1 (2026-05-14): credit_analyst is broker-family in claims
        // but does NOT require an office or report selection — the role is
        // primarily a commission-recipient marker. broker / broker_assistant
        // still require both. Operational-only credit_analyst records
        // (no email) never reach createInvitation and have no auth claims
        // at all, so offices / reports are nonsensical there.
        const requiresBrokerScope =
            inviteRole === 'broker' || inviteRole === 'broker_assistant';
        if (requiresBrokerScope && inviteAccessScope.length === 0) {
            setInviteMessage({ type: 'error', text: 'Please select at least one office for the broker.' });
            setIsSubmitting(false);
            return;
        }
        if (requiresBrokerScope && reportAccessMode === 'custom' && selectedReports.length === 0) {
            setInviteMessage({ type: 'error', text: 'Please select at least one report, or switch to "All Reports" mode.' });
            setIsSubmitting(false);
            return;
        }
        try {
            // PR 1A-v3 (2026-05-13): email-optional path for credit_analyst.
            // When the email field is blank for a credit_analyst, dispatch
            // to `manageCreditAnalystRecord` (operational-only record, no
            // login) instead of `createInvitation` (login-bearing).
            // - Clean two-endpoint design: each callable does one thing.
            // - createInvitation contract stays unchanged (email required).
            // - linkedBrokerDocIds + display name flow into the analyst
            //   record so the intent is captured even without an auth user.
            //
            // PR 1A-v3.1 (2026-05-14): firstName + surname required for both
            // login and operational-only credit_analyst invites. Combined
            // into displayName for the canonical record.
            const emailTrimmed = inviteEmail.trim();
            const firstTrimmed = inviteFirstName.trim();
            const surnameTrimmed = inviteSurname.trim();
            const combinedDisplayName = `${firstTrimmed} ${surnameTrimmed}`.trim();

            if (inviteRole === 'credit_analyst') {
                if (!firstTrimmed || !surnameTrimmed) {
                    setInviteMessage({
                        type: 'error',
                        text: 'First name and surname are required for credit analysts.',
                    });
                    setIsSubmitting(false);
                    return;
                }
            }

            if (inviteRole === 'credit_analyst' && !emailTrimmed) {
                if (!userData?.companyId) {
                    setInviteMessage({ type: 'error', text: 'Missing company context.' });
                    setIsSubmitting(false);
                    return;
                }
                const manageFn = httpsCallable(functions, 'manageCreditAnalystRecord');
                await manageFn({
                    action: 'create',
                    companyId: userData.companyId,
                    displayName: combinedDisplayName,
                    email: null,
                    notes: inviteNotes.trim() || null,
                    ...(canConfigureAnalystLinkage && inviteLinkedBrokerDocIds.length > 0
                        ? { linkedBrokerDocIds: inviteLinkedBrokerDocIds }
                        : {}),
                });
                setInviteMessage({
                    type: 'success',
                    text: `Operational credit analyst record created for ${combinedDisplayName}. No email sent (no login).`,
                });
                // Clear the adopt-existing selection so it cannot ride along
                // onto the next invite sent from this still-open modal.
                setSelectedAnalystId('');
                setIsSubmitting(false);
                return;
            }

            const invitationData: any = { email: emailTrimmed, role: inviteRole };
            // Adopt-existing linkage. Only sent when the admin explicitly
            // picked a record; the key is omitted entirely otherwise (never
            // null, which a callable would encode from undefined).
            if (inviteRole === 'credit_analyst' && selectedAnalystId) {
                invitationData.analystId = selectedAnalystId;
            }
            if (isBrokerFamilyRole) {
                // PR 1A-v3.1: brokerDocId is optional for credit_analyst —
                // only include it when a broker was actually selected so we
                // don't send '' to a backend field that expects a real doc id.
                if (selectedBrokerId) {
                    invitationData.brokerDocId = selectedBrokerId;
                }
                invitationData.hideCommissions = inviteHideCommissions;
                // Only meaningful when commissions are hidden; never send true otherwise.
                invitationData.hideLoanAmounts = inviteHideCommissions && inviteHideLoanAmounts;
                invitationData.assignedOffices = inviteAccessScope;
                // PR 1A-v3.1: for credit_analyst, the inviter-supplied
                // first+surname is the authoritative name. expectedBrokerMetadata
                // is the existing carrier that surfaces on acceptInvitation,
                // so we route the combined name through it. For broker /
                // broker_assistant invites the original auto-filled
                // inviteExpectedName (from the broker doc) is correct.
                const approximateName =
                    inviteRole === 'credit_analyst' && combinedDisplayName
                        ? combinedDisplayName
                        : inviteExpectedName.trim();
                invitationData.expectedBrokerMetadata = {
                    approximateName,
                    officeId: inviteAccessScope[0],
                    notes: inviteNotes.trim() || undefined,
                };
                // Granular access controls
                if (reportAccessMode === 'custom' && selectedReports.length > 0) {
                    invitationData.allowedReports = selectedReports;
                }
                if (templateAccessMode === 'custom' && selectedLoanBookIds.length > 0) {
                    invitationData.allowedLoanBookIds = selectedLoanBookIds;
                }
                // Broker-family: optional multi-broker linkage (super_admin/administrator only,
                // enforced server-side as well in createInvitation).
                if (isBrokerFamilyRole && canConfigureAnalystLinkage && inviteLinkedBrokerDocIds.length > 0) {
                    invitationData.linkedBrokerDocIds = inviteLinkedBrokerDocIds;
                }
            }
            if (inviteRole === 'marketing') {
                // Marketing default: empty allowedReports (lands on Lead Inbox), hidden commissions,
                // office-scoped via inviteAccessScope. Admin can grant reports later via Edit Permissions.
                invitationData.assignedOffices = inviteAccessScope;
                invitationData.hideCommissions = true;
                invitationData.canSeeAllOffices = false;
                if (reportAccessMode === 'custom' && selectedReports.length > 0) {
                    invitationData.allowedReports = selectedReports;
                }
            }
            if (inviteRole === 'super_admin') {
                // super_admin is company-wide by role (resolveOfficeScope grants full
                // scope on the role alone), so the invite must record that explicitly.
                // With no branch here the payload carried neither key, createInvitation
                // stored `!!undefined` = false, and the owner landed with
                // `officeIds: []` + `canSeeAllOffices: false`: harmless to RBAC but
                // read as "no scope" by every claims-inspecting surface.
                invitationData.canSeeAllOffices = true;
                invitationData.assignedOffices = [];
            }
            if (inviteRole === 'office_admin' || inviteRole === 'administrator') {
                invitationData.canSeeAllOffices = assignAllOffices;
                invitationData.assignedOffices = assignAllOffices ? [] : inviteAccessScope;
            }
            const createInvitation = httpsCallable(functions, 'createInvitation');
            const result = await createInvitation(invitationData);
            const data = result.data as { message: string };
            setInviteMessage({ type: 'success', text: data.message });
            // This modal stays open on success, so clear the adopt-existing
            // selection: the record now carries an open invitation, and a
            // silent carry-over would fail the next send with
            // analyst_record_already_invited.
            setSelectedAnalystId('');
        } catch (err: any) {
            console.error("Invitation error:", err);
            setInviteMessage({ type: 'error', text: err.message || 'Failed to send invitation.' });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleScopeChange = (officeId: any) => {
        setInviteAccessScope(prev => 
            prev.includes(officeId) 
            ? prev.filter((id: any) => id !== officeId)
            : [...prev, officeId]
        );
    };

const handleStatusChange = (targetUid: string, currentStatus: string) => {
        // Prevent updates if one is already in progress
        if (pending[targetUid]) return;

        // The JSX already disables the button for the current user, but this is a good safeguard.
        if (!user || targetUid === user.uid) {
            toast.error("You cannot change your own status.");
            return;
        }

        const newStatus = currentStatus === 'active' ? 'revoked' : 'active';
        const actionVerb = newStatus === 'revoked' ? 'revoke' : 'restore';

        setConfirmDialog({
            isOpen: true,
            title: `${newStatus === 'revoked' ? 'Revoke' : 'Restore'} User Access`,
            message: `Are you sure you want to ${actionVerb} this user's access?`,
            confirmLabel: newStatus === 'revoked' ? 'Revoke Access' : 'Restore Access',
            variant: newStatus === 'revoked' ? 'danger' : 'primary',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setPending((p: any) => ({ ...p, [targetUid]: true }));
                try {
                    const result = await updateUserStatus(targetUid, newStatus);
                    // See TeamManagementV2Page: revoked with the session revoke
                    // unproven is an error, not a success.
                    if (
            newStatus === 'revoked' &&
            (result?.sessionEnded === false ||
              result?.claimsCleared === false ||
              result?.revokeRaced === true ||
              result?.invitationsSwept === false)
          ) {
                        toast.error(revokeWarning(result), { duration: 12000 });
                    } else {
                        toast.success(`User access ${newStatus === 'revoked' ? 'revoked' : 'restored'} successfully`);
                    }
                } catch (err: any) {
                    console.error("Failed to update status:", err);
                    toast.error(`Failed to update status: ${err.message}`);
                } finally {
                    setPending((p: any) => {
                        const { [targetUid]: _, ...rest } = p;
                        return rest;
                    });
                }
            },
        });
    };

    const handleRoleChange = (targetUid: string, currentRole: string, newRole: string) => {
        // Prevent updates if one is already in progress for this user
        if (pendingRoleChange[targetUid]) return;

        // If the role hasn't actually changed, do nothing
        if (currentRole === newRole) return;

        const displayRole = newRole.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

        setConfirmDialog({
            isOpen: true,
            title: 'Change User Role',
            message: `Are you sure you want to change this user's role to ${displayRole}?`,
            confirmLabel: 'Change Role',
            variant: 'primary',
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, isOpen: false }));
                setPendingRoleChange((p: any) => ({ ...p, [targetUid]: true }));
                try {
                    await updateUserRole(targetUid, newRole);
                    toast.success(`Role updated to ${displayRole}`);
                } catch (err: any) {
                    console.error("Failed to update role:", err);
                    toast.error(`Failed to update role: ${err.message}`);
                } finally {
                    setPendingRoleChange((p: any) => {
                        const { [targetUid]: _, ...rest } = p;
                        return rest;
                    });
                }
            },
        });

        // If user closes the dialog without confirming, reset dropdown
        // The onCancel handler already sets isOpen: false
    };

    const formatDate = (timestamp: any) => timestamp?.toDate().toLocaleDateString() || 'N/A';

    const getExpirationStatus = (expiresAt: any) => {
        if (!expiresAt) return { text: 'Unknown', color: 'bg-gray-200 text-gray-800' };

        const expiryDate = expiresAt.toDate();
        const now = new Date();
        const daysLeft = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (daysLeft < 0) {
            return { text: 'Expired', color: 'bg-red-200 text-red-800' };
        } else if (daysLeft < 1) {
            return { text: '<1 day left', color: 'bg-red-200 text-red-800' };
        } else if (daysLeft <= 2) {
            return { text: `${daysLeft} days left`, color: 'bg-yellow-200 text-yellow-800' };
        } else {
            return { text: `${daysLeft} days left`, color: 'bg-green-200 text-green-800' };
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                icon={UsersRound}
                title="Team Management"
                subtitle="Manage access control for your organization"
                actions={
                    <div className="flex items-center gap-2">
                        <HelpButton pageKey="team" />
                        <button
                            onClick={() => setShowInviteModal(true)}
                            {...brokerFilteredAdminProps(callerIsBrokerFiltered)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:cursor-not-allowed disabled:bg-gray-600 disabled:hover:bg-gray-600"
                        >
                            <Plus className="w-5 h-5" /> Invite User
                        </button>
                    </div>
                }
            />

            {/* Tab Navigation */}
            <div className="mb-6 border-b border-gray-700">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('invitations')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'invitations'
                                ? 'border-blue-500 text-blue-500'
                                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                    >
                        Pending Invitations ({invitations.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('users')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'users'
                                ? 'border-blue-500 text-blue-500'
                                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                    >
                        Active Users ({visibleMembers?.length || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('broker_approvals')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                            activeTab === 'broker_approvals'
                                ? 'border-blue-500 text-blue-500'
                                : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                        }`}
                    >
                        Broker Approvals ({brokerApprovals.length})
                    </button>
                    {canManageAssignments && (
                        <button
                            onClick={() => setActiveTab('broker_assignments')}
                            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'broker_assignments'
                                    ? 'border-blue-500 text-blue-500'
                                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            }`}
                        >
                            Broker Assignments ({assignments.filter(a => a.status === 'active').length})
                        </button>
                    )}
                    {canManageOperationalRoles && (
                        <button
                            onClick={() => setActiveTab('operational_roles')}
                            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'operational_roles'
                                    ? 'border-blue-500 text-blue-500'
                                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                            }`}
                        >
                            Operational Roles ({operationalAnalysts.filter(a => a.status === 'active').length})
                        </button>
                    )}
                </nav>
            </div>

            {/* Tab 1: Pending Invitations */}
            {activeTab === 'invitations' && (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-4">Pending Invitations</h3>
                {invitationsLoading ? (
                    <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                        <p>Loading invitations...</p>
                    </div>
                ) : invitationsError ? (
                    <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-red-400">
                        <p>{invitationsError}</p>
                    </div>
                ) : invitationRows.length === 0 ? (
                    <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                        <p>No pending invitations</p>
                    </div>
                ) : (
                    <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-400">
                            <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                                <tr>
                                    <th scope="col" className="px-6 py-3">Email</th>
                                    <th scope="col" className="px-6 py-3">Role</th>
                                    <th scope="col" className="px-6 py-3">Invited By</th>
                                    <th scope="col" className="px-6 py-3">Sent Date</th>
                                    <th scope="col" className="px-6 py-3">Expires</th>
                                    <th scope="col" className="px-6 py-3">Status</th>
                                    <th scope="col" className="px-6 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invitationRows.map((invitation) => {
                                    const expirationStatus = getExpirationStatus(invitation.expiresAt);
                                    const unfinishedSetup = isUnfinishedInvitationSetup(invitation);
                                    // WS8b M5 (V6-03): which of the two presses this row is on.
                                    const press = unfinishedSetup ? unfinishedSetupPress(invitation) : null;
                                    return (
                                        <tr key={invitation.id} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-white">{invitation.email}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="capitalize">{invitation.role.replace('_', ' ')}</span>
                                            </td>
                                            <td className="px-6 py-4">{invitation.invitedBy || 'Unknown'}</td>
                                            <td className="px-6 py-4">{formatDate(invitation.createdAt)}</td>
                                            <td className="px-6 py-4">{formatDate(invitation.expiresAt)}</td>
                                            <td className="px-6 py-4">
                                                {/* WS8b R8 (V2-05): a PARKED invitation is one the invitee
                                                    has already opened and is setting up two-factor for. It is
                                                    exempt from the expiry sweep by design, so an expiry badge
                                                    would be actively misleading; show the real state instead. */}
                                                {press ? (
                                                    /* WS8b M2 (V5-02): granted, but the finalisation did not
                                                       finish. Distinct from "Setting up two-factor": this
                                                       person already HAS access. M5 (V6-03): the badge also
                                                       distinguishes the two presses, because after the first
                                                       one the session is dead and only the incomplete setup
                                                       is left. */
                                                    <span className="inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full bg-amber-900/60 text-amber-200">
                                                        {unfinishedSetupBadgeLabel(press)}
                                                    </span>
                                                ) : invitation.status === 'enrolment_pending' ? (
                                                    <span className="inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full bg-blue-900/60 text-blue-200">
                                                        Setting up two-factor
                                                    </span>
                                                ) : (
                                                    <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full ${expirationStatus.color}`}>
                                                        {expirationStatus.text}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleCancelInvitation(invitation.id, invitation.email, press)}
                                                    disabled={callerIsBrokerFiltered || cancellingInvitation[invitation.id]}
                                                    title={callerIsBrokerFiltered ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {press
                                                        ? unfinishedSetupActionLabel(press, Boolean(cancellingInvitation[invitation.id]))
                                                        : (cancellingInvitation[invitation.id] ? 'Cancelling...' : 'Cancel')}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                </div>
            )}

            {/* Tab 2: Active Users */}
            {activeTab === 'users' && (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-4">Active Users</h3>
                    {error ? <div className="p-8 text-center text-red-400"><p>{error}</p></div> : (
                        <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-400">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">User</th>
                                        <th scope="col" className="px-6 py-3">Role</th>
                                        <th scope="col" className="px-6 py-3">Access Scope</th>
                                        <th scope="col" className="px-6 py-3">Joined</th>
                                        <th scope="col" className="px-6 py-3">Status</th>
                                        <th scope="col" className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleMembers?.map((member: any) => (
                                        <tr key={member.id} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-white">{member.displayName}</div>
                                                <div className="text-gray-500">{member.email}</div>
                                                {member.role === 'broker_assistant' && member.brokerDocId && (
                                                    <div className="text-xs text-purple-400 mt-1">
                                                        Assistant to: {brokerDocs.find((b: any) => b.id === member.brokerDocId)?.currentName || 'Unknown Broker'}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {!canManageRole(userData?.role || '', !!claims?.isFounder, member.role) ? (
                                                    <span className="bg-gray-700 border border-gray-600 rounded-md px-2 py-1 text-sm opacity-50">
                                                        {member.role.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                                    </span>
                                                ) : (
                                                    <select
                                                        key={`${member.id}-${member.role}-${pendingRoleChange[member.id]}`}
                                                        value={member.role}
                                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleRoleChange(member.id, member.role, e.target.value)}
                                                        disabled={!!pendingRoleChange[member.id]}
                                                        className="bg-gray-700 border border-gray-600 rounded-md p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {assignableRoles.map((role: string) => (
                                                            <option key={role} value={role}>
                                                                {role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                            </option>
                                                        ))}
                                                    </select>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                {(member.canSeeAllOffices || member.role === 'super_admin') ? 'All Offices' : (() => {
                                                    const assignedOfficeNames = (member.assignedOffices || [])
                                                        .map((id: any) => offices?.find(o => o.id === id)?.name)
                                                        .filter((name: any) => name);
                                                    return assignedOfficeNames.length > 0
                                                        ? assignedOfficeNames.join(', ')
                                                        : (member.assignedOffices?.length > 0 ? 'Offices not found' : 'No offices assigned');
                                                })()}
                                            </td>
                                            <td className="px-6 py-4">{formatDate(member.createdAt)}</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full ${member.status === 'active' ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                                                    {member.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleStatusChange(member.id, member.status)}
                                                        disabled={!user || member.id === user.uid || !canManageRole(userData?.role || '', !!claims?.isFounder, member.role) || !!pending[member.id]}
                                                        aria-disabled={!user || member.id === user.uid || !canManageRole(userData?.role || '', !!claims?.isFounder, member.role) || !!pending[member.id]}
                                                        aria-busy={!!pending[member.id]}
                                                        className={`px-3 py-1 rounded-md text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                                            member.status === 'active'
                                                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                                                : 'bg-green-600 hover:bg-green-700 text-white'
                                                        }`}
                                                    >
                                                        {pending[member.id] ? 'Working…' : (member.status === 'active' ? 'Revoke' : 'Restore')}
                                                    </button>
                                                    {((canManageAssignments || canConfigureAnalystLinkage) && (member.role === 'broker' || member.role === 'broker_assistant' || member.role === 'credit_analyst')) && (
                                                        <>
                                                            <button
                                                                onClick={() => handleToggleCommissionVisibility(member.id, !!member.hideCommissions)}
                                                                disabled={!!togglingVisibility[member.id]}
                                                                title={member.hideCommissions ? 'Commissions hidden — click to show' : 'Commissions visible — click to hide'}
                                                                className={`px-2 py-1 rounded-md text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                    member.hideCommissions
                                                                        ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                                                        : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                                                                }`}
                                                            >
                                                                {togglingVisibility[member.id] ? '...' : member.hideCommissions ? '$ Hidden' : '$ Visible'}
                                                            </button>
                                                            {member.hideCommissions && (
                                                                <button
                                                                    onClick={() => handleToggleLoanAmountVisibility(member.id, !!member.hideLoanAmounts)}
                                                                    disabled={!!togglingVisibility[member.id]}
                                                                    title={member.hideLoanAmounts ? 'Loan amounts hidden in report tables. Click to show' : 'Loan amounts visible in report tables. Click to hide'}
                                                                    className={`px-2 py-1 rounded-md text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                        member.hideLoanAmounts
                                                                            ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                                                            : 'bg-gray-600 hover:bg-gray-700 text-gray-300'
                                                                    }`}
                                                                >
                                                                    {togglingVisibility[member.id] ? '...' : member.hideLoanAmounts ? 'Loan $ Hidden' : 'Loan $ Visible'}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => openEditPermissions(member)}
                                                                title="Edit report and loan book access permissions"
                                                                className="px-2 py-1 rounded-md text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white"
                                                            >
                                                                Permissions
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Tab 3: Broker Approvals */}
            {activeTab === 'broker_approvals' && (
                <div className="mb-8">
                    <h3 className="text-2xl font-bold mb-4">Broker Identity Verification</h3>
                    <p className="text-gray-400 mb-2">
                        Review and approve broker users linking their accounts to commission data.
                    </p>
                    <p className="text-xs text-gray-500 mb-6">
                        Brokers with high-confidence identity matches are auto-linked during onboarding and do not appear here.
                    </p>
                    {approvalsLoading ? (
                        <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                            <p>Loading broker approvals...</p>
                        </div>
                    ) : approvalsError ? (
                        <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-red-400">
                            <p>{approvalsError}</p>
                        </div>
                    ) : brokerApprovals.length === 0 ? (
                        <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                            <p>No pending broker approvals</p>
                        </div>
                    ) : (
                        <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-400">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">Broker Name (Data)</th>
                                        <th scope="col" className="px-6 py-3">User Account</th>
                                        <th scope="col" className="px-6 py-3">Match Score</th>
                                        <th scope="col" className="px-6 py-3">Requested</th>
                                        <th scope="col" className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {brokerApprovals.map((approval) => {
                                        const score = approval.confidenceScore || 0;
                                        const scoreColor = score >= 0.9 ? 'text-green-400' : score >= 0.7 ? 'text-yellow-400' : 'text-red-400';
                                        return (
                                            <tr key={approval.id} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-white">{approval.brokerName}</div>
                                                    {approval.brokerId && (
                                                        <div className="text-xs text-gray-500">ID: {approval.brokerId}</div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="font-medium text-white">{approval.userDisplayName}</div>
                                                    <div className="text-xs text-gray-500">{approval.userEmail}</div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`font-semibold ${scoreColor}`}>
                                                        {(score * 100).toFixed(0)}%
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    {formatDate(approval.createdAt)}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleApproveBroker(approval.id, approval.brokerName)}
                                                            disabled={processingApproval[approval.id]}
                                                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {processingApproval[approval.id] ? 'Approving...' : 'Approve'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleRejectBroker(approval.id, approval.brokerName)}
                                                            disabled={processingApproval[approval.id]}
                                                            className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {processingApproval[approval.id] ? 'Rejecting...' : 'Reject'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
            {/* Tab 4: Broker Assignments (super_admin + office_admin only) */}
            {activeTab === 'broker_assignments' && canManageAssignments && (
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-2xl font-bold">Broker Loan Assignments</h3>
                            <p className="text-gray-400 mt-1">Manage departing broker loan book assignments.</p>
                        </div>
                        <button
                            onClick={() => setShowAssignModal(true)}
                            {...brokerFilteredAdminProps(callerIsBrokerFiltered)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:cursor-not-allowed disabled:bg-gray-600 disabled:hover:bg-gray-600"
                        >
                            <Plus className="w-5 h-5" /> Assign Broker's Loans
                        </button>
                    </div>
                    {assignmentsLoading ? (
                        <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                            <p>Loading assignments...</p>
                        </div>
                    ) : assignments.length === 0 ? (
                        <div className="bg-gray-800 rounded-lg shadow-lg p-8 text-center text-gray-400">
                            <p>No broker loan assignments</p>
                        </div>
                    ) : (
                        <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-400">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                                    <tr>
                                        <th scope="col" className="px-6 py-3">Source Broker</th>
                                        <th scope="col" className="px-6 py-3">Assigned To</th>
                                        <th scope="col" className="px-6 py-3">Reason</th>
                                        <th scope="col" className="px-6 py-3">Status</th>
                                        <th scope="col" className="px-6 py-3">Effective From</th>
                                        <th scope="col" className="px-6 py-3">Effective To</th>
                                        <th scope="col" className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignments.map((assignment) => (
                                        <tr key={assignment.id} className="bg-gray-800 border-b border-gray-700 hover:bg-gray-750">
                                            <td className="px-6 py-4 font-medium text-white">{getBrokerName(assignment.sourceBrokerDocId)}</td>
                                            <td className="px-6 py-4 font-medium text-white">{getBrokerName(assignment.assignedToBrokerDocId)}</td>
                                            <td className="px-6 py-4 capitalize">{assignment.reason.replace(/_/g, ' ')}</td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full ${
                                                    assignment.status === 'active'
                                                        ? 'bg-green-200 text-green-800'
                                                        : 'bg-red-200 text-red-800'
                                                }`}>
                                                    {assignment.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">{assignment.effectiveFrom?.toDate?.()?.toLocaleDateString() || 'N/A'}</td>
                                            <td className="px-6 py-4">{assignment.effectiveTo?.toDate?.()?.toLocaleDateString() || '\u2014'}</td>
                                            <td className="px-6 py-4">
                                                {assignment.status === 'active' && (
                                                    <button
                                                        onClick={() => handleRevokeAssignment(assignment.id, getBrokerName(assignment.sourceBrokerDocId))}
                                                        disabled={callerIsBrokerFiltered || !!revokingAssignment[assignment.id]}
                                                        title={callerIsBrokerFiltered ? BROKER_FILTERED_ADMIN_COPY : undefined}
                                                        className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {revokingAssignment[assignment.id] ? 'Revoking...' : 'Revoke'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Tab 5: Operational Roles (super_admin / administrator only).
                Credit Analyst commission-recipient records — with optional
                login. Per-broker share allocations live on the broker's
                commission settings page (Credit Analysts section). */}
            {activeTab === 'operational_roles' && canManageOperationalRoles && (
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <div>
                            <h3 className="text-2xl font-bold">Operational Roles</h3>
                            <p className="text-gray-400 mt-1">
                                Credit analyst records — both login (Firebase Auth user) and operational-only (no login) live here. To create a new credit analyst use <span className="font-semibold text-blue-300">Invite User</span> at the top of this page; leave email blank for an operational-only record.
                            </p>
                        </div>
                    </div>

                    {/* Next-step callout — visible whenever there's at least one
                        analyst, so admins know where to go after creating one.
                        Configuring per-broker share lives on each broker's
                        commission settings page (Credit Analysts section). */}
                    {operationalAnalysts.filter((a) => a.status === 'active').length > 0 && (
                        <div className="mb-4 p-3 rounded-lg border border-blue-500/30 bg-blue-500/5 flex items-start gap-3">
                            <div className="flex-1 text-sm text-blue-300">
                                <span className="font-semibold">Next step:</span> open a broker's commission settings page →{' '}
                                <span className="font-semibold">Credit Analysts</span> section → click <span className="font-semibold">Add Credit Analyst</span> → pick an analyst from this list and set their share of that broker's earned commission.
                            </div>
                            <a
                                href="/broker-commission-settings"
                                className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                            >
                                Open Broker Commission Settings →
                            </a>
                        </div>
                    )}

                    {operationalLoading ? (
                        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">Loading credit analysts…</div>
                    ) : operationalAnalysts.length === 0 ? (
                        <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
                            No credit analyst records yet. To add one, click <span className="font-semibold text-blue-300">Invite User</span> above — leave the email blank for an operational-only record.
                        </div>
                    ) : (
                        <div className="bg-gray-800 rounded-lg shadow-lg overflow-x-auto">
                            <table className="w-full text-sm text-left text-gray-400">
                                <thead className="text-xs text-gray-400 uppercase bg-gray-700">
                                    <tr>
                                        <th className="px-6 py-3">Name</th>
                                        <th className="px-6 py-3">Email</th>
                                        <th className="px-6 py-3">Login</th>
                                        <th className="px-6 py-3">Status</th>
                                        <th className="px-6 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {operationalAnalysts.map((analyst) => (
                                        <tr
                                            key={analyst.analystId}
                                            className={`bg-gray-800 border-b border-gray-700 ${analyst.status === 'archived' ? 'opacity-50' : ''}`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="font-medium text-white">{analyst.displayName}</div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {analyst.email || <span className="text-gray-600 italic">none</span>}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-2 py-0.5 text-[0.625rem] font-bold rounded border ${analyst.hasLogin ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                                                    {analyst.hasLogin ? 'YES' : 'NO'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-block px-3 py-1 text-xs font-semibold uppercase rounded-full ${analyst.status === 'active' ? 'bg-green-200 text-green-800' : 'bg-gray-300 text-gray-700'}`}>
                                                    {analyst.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <button
                                                    onClick={() => handleArchiveAnalystRecord(analyst.analystId, analyst.status)}
                                                    className={`px-3 py-1 rounded-md text-xs font-semibold ${analyst.status === 'active' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white`}
                                                >
                                                    {analyst.status === 'active' ? 'Archive' : 'Restore'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Assignment Creation Modal */}
            <Modal title="Assign Broker's Loans" show={showAssignModal} onClose={() => { setShowAssignModal(false); setAssignError(''); }}>
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Source Broker (departing)</label>
                        <select
                            value={assignSource}
                            onChange={(e) => { setAssignSource(e.target.value); if (e.target.value === assignTarget) setAssignTarget(''); }}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                        >
                            <option value="">Select broker...</option>
                            {brokerDocs
                                .filter(b => !b.mergedInto && b.status !== 'inactive')
                                .filter(b => !activeSources.has(b.id))
                                .map(b => (
                                    <option key={b.id} value={b.id}>{b.currentName}</option>
                                ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Assign To Broker</label>
                        <select
                            value={assignTarget}
                            onChange={(e) => setAssignTarget(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                        >
                            <option value="">Select broker...</option>
                            {brokerDocs
                                .filter(b => !b.mergedInto && b.status !== 'inactive')
                                .filter(b => b.id !== assignSource)
                                .map(b => (
                                    <option key={b.id} value={b.id}>{b.currentName}</option>
                                ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Reason</label>
                        <select
                            value={assignReason}
                            onChange={(e) => setAssignReason(e.target.value as AssignmentReason)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                        >
                            <option value="broker_departure">Broker Departure</option>
                            <option value="leave_of_absence">Leave of Absence</option>
                            <option value="restructure">Restructure</option>
                            <option value="retirement">Retirement</option>
                            <option value="other">Other</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Notes (optional)</label>
                        <textarea
                            value={assignNotes}
                            onChange={(e) => setAssignNotes(e.target.value)}
                            rows={3}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                            placeholder="Any additional context..."
                        />
                    </div>
                    {assignError && (
                        <div className="p-3 rounded-md text-sm bg-red-900 text-red-300">{assignError}</div>
                    )}
                    <div className="flex justify-end pt-4">
                        <button
                            onClick={handleCreateAssignment}
                            disabled={callerIsBrokerFiltered || !assignSource || !assignTarget || assignSubmitting}
                            title={callerIsBrokerFiltered ? BROKER_FILTERED_ADMIN_COPY : undefined}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                        >
                            {assignSubmitting ? 'Creating...' : 'Create Assignment'}
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal title="Invite User" show={showInviteModal} onClose={() => { setShowInviteModal(false); setSelectedAnalystId(''); }}>
                <form onSubmit={handleInviteSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="invite-email" className="block text-sm font-medium text-gray-300 mb-2">
                            Email Address
                            {inviteRole === 'credit_analyst' && (
                                <span className="text-gray-500 font-normal"> (optional)</span>
                            )}
                        </label>
                        <input
                            id="invite-email"
                            type="email"
                            value={inviteEmail}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                            placeholder={inviteRole === 'credit_analyst' ? 'user@example.com (leave blank for operational-only)' : 'user@example.com'}
                            required={inviteRole !== 'credit_analyst'}
                        />
                        {inviteRole === 'credit_analyst' && (
                            <p className="text-xs text-gray-400 mt-1.5">
                                Leave blank to create an operational-only record (no login, no email sent). Provide an email to send a login invitation. The analyst can be allocated commission on broker commission settings either way.
                            </p>
                        )}
                    </div>

                    <div>
                        <label htmlFor="invite-role" className="block text-sm font-medium text-gray-300 mb-2">Assign Role</label>
                        <select id="invite-role" value={inviteRole} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setInviteRole(e.target.value); setSelectedBrokerId(''); setInviteHideCommissions(true); setInviteHideLoanAmounts(false); setInviteExpectedName(''); setInviteFirstName(''); setInviteSurname(''); setInviteAccessScope([]); setAssignAllOffices(false); setReportAccessMode('custom'); setSelectedReports([]); setTemplateAccessMode('custom'); setSelectedLoanBookIds([]); setInviteLinkedBrokerDocIds([]); setSelectedAnalystId(''); }} className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3">
                            {assignableRoles.map((role: string) => (
                                <option key={role} value={role}>
                                    {role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* PR 1A-v3.1 (2026-05-14): explicit name inputs for
                        credit_analyst. Required for ALL credit_analyst invites
                        — both login (uses these to populate users/{uid}.displayName
                        and credit_analysts/{uid}.displayName) and operational-only
                        (no other way to identify the record on the broker
                        commission settings page). */}
                    {inviteRole === 'credit_analyst' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label htmlFor="invite-firstname" className="block text-sm font-medium text-gray-300 mb-2">
                                    First Name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    id="invite-firstname"
                                    type="text"
                                    value={inviteFirstName}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteFirstName(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                                    placeholder="e.g. Hanah"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="invite-surname" className="block text-sm font-medium text-gray-300 mb-2">
                                    Surname <span className="text-red-400">*</span>
                                </label>
                                <input
                                    id="invite-surname"
                                    type="text"
                                    value={inviteSurname}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteSurname(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                                    placeholder="e.g. Smith"
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Adopt-existing linkage: an invitation can claim an EXISTING
                        login-less analyst record instead of minting a new one on
                        accept, so commission allocations and history keyed on that
                        record stay attached when the person gains a login. Only
                        offered once an email is present, since an operational-only
                        record (blank email) never reaches createInvitation. */}
                    {inviteRole === 'credit_analyst' && inviteEmail.trim() !== '' && (
                        <div>
                            <label htmlFor="invite-analyst-record" className="block text-sm font-medium text-gray-300 mb-2">
                                Link to Existing Analyst Record (Optional)
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                                If this person already has an operational record, select it. Their login is added to that record on accept, so commission allocations and history stay attached to it. Leave unselected to create a new record.
                            </p>
                            <select
                                id="invite-analyst-record"
                                value={selectedAnalystId}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedAnalystId(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                                disabled={adoptableAnalysts.length === 0}
                            >
                                <option value="">-- Create a new record --</option>
                                {adoptableAnalysts.map((analyst) => (
                                    <option key={analyst.analystId} value={analyst.analystId}>
                                        {analyst.displayName}
                                    </option>
                                ))}
                            </select>
                            {adoptableAnalysts.length === 0 && (
                                <p className="text-xs text-gray-500 mt-1.5">
                                    No login-less analyst records available to link.
                                </p>
                            )}
                        </div>
                    )}

                    {isBrokerFamilyRole && (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="invite-broker-select" className="block text-sm font-medium text-gray-300 mb-2">
                                    {inviteRole === 'credit_analyst' ? 'Select Broker (Optional)' : <>Select Broker <span className="text-red-400">*</span></>}
                                </label>
                                <p className="text-xs text-gray-400 mb-2">
                                    {inviteRole === 'credit_analyst'
                                        ? "Optional — select the analyst's primary broker doc if their name appears in lender files. Commission allocations are configured per-broker via the Credit Analysts section on the broker's commission settings page."
                                        : "Select the broker from your company's broker list. Their office will be auto-filled."}
                                </p>
                                <select
                                    id="invite-broker-select"
                                    value={selectedBrokerId}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        const brokerId = e.target.value;
                                        setSelectedBrokerId(brokerId);
                                        if (brokerId) {
                                            const broker = brokerDocs.find(b => b.id === brokerId);
                                            if (broker) {
                                                setInviteExpectedName(broker.currentName);
                                                if (broker.currentOfficeId) {
                                                    setInviteAccessScope([broker.currentOfficeId]);
                                                }
                                                // Auto-select primary loan book for this broker. For legacy mode
                                                // loan_book_id === fileTemplate_id so primaryTemplateId may match a
                                                // loan_book directly; for unified mode it's the shared template and
                                                // won't match any loan_book_id — skip auto-select in that case.
                                                const primaryTemplateId = companyData?.primaryTemplateId;
                                                if (primaryTemplateId && loanBooks.some((lb) => lb.id === primaryTemplateId)) {
                                                    setTemplateAccessMode('custom');
                                                    setSelectedLoanBookIds([primaryTemplateId]);
                                                }
                                            }
                                        } else {
                                            setInviteExpectedName('');
                                            setInviteAccessScope([]);
                                            setSelectedLoanBookIds([]);
                                        }
                                    }}
                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                                    required={inviteRole !== 'credit_analyst'}
                                >
                                    <option value="">-- Select a broker --</option>
                                    {brokerDocs
                                        .filter(b => !b.mergedInto && b.status !== 'inactive')
                                        .sort((a, b) => a.currentName.localeCompare(b.currentName))
                                        .map((broker) => (
                                            <option key={broker.id} value={broker.id}>
                                                {broker.currentName}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            {/* Commission Visibility Toggle */}
                            <div className={`border rounded-lg p-4 transition-colors ${
                                inviteHideCommissions
                                    ? 'border-amber-500/50 bg-amber-500/5'
                                    : 'border-green-500/50 bg-green-500/5'
                            }`}>
                                <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                        {inviteHideCommissions
                                            ? <EyeOff className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                                            : <Eye className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                                        }
                                        <div>
                                            <p className="text-sm font-medium text-white">Commission Visibility</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {inviteHideCommissions
                                                    ? 'Commission dollar values will be hidden from this broker. They can still see their loan portfolio and reports.'
                                                    : 'This broker will see full commission dollar values in reports and dashboards.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setInviteHideCommissions(!inviteHideCommissions)}
                                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                            inviteHideCommissions ? 'bg-amber-500' : 'bg-green-500'
                                        }`}
                                    >
                                        <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                            inviteHideCommissions ? 'translate-x-5' : 'translate-x-0'
                                        }`} />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 mt-2 ml-8">
                                    {inviteHideCommissions ? 'Hidden (recommended)' : 'Visible'}
                                </p>
                                {/* Loan Amounts sub-toggle: only relevant when commissions are hidden.
                                    Loan values (not commission data) show in report tables by default. */}
                                {inviteHideCommissions && (
                                    <div className="mt-3 ml-8 pt-3 border-t border-gray-700 flex items-start justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-white">Loan Amounts</p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {inviteHideLoanAmounts
                                                    ? 'Loan amounts and balances will also be hidden in report tables.'
                                                    : 'Loan amounts and balances remain visible in report tables (commissions stay hidden).'}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setInviteHideLoanAmounts(!inviteHideLoanAmounts)}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                                                inviteHideLoanAmounts ? 'bg-amber-500' : 'bg-green-500'
                                            }`}
                                        >
                                            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                inviteHideLoanAmounts ? 'translate-x-5' : 'translate-x-0'
                                            }`} />
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Report Access Control */}
                            <div className="border border-gray-600 rounded-lg p-4">
                                <div className="flex items-start gap-3 mb-4">
                                    <FileBarChart className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-white">Report Access</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Select which reports this broker can view. No reports are selected by default.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 mb-3">
                                    <button
                                        type="button"
                                        onClick={() => { setReportAccessMode('custom'); setSelectedReports([]); }}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            reportAccessMode === 'custom'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-700 text-gray-400 hover:text-gray-300'
                                        }`}
                                    >
                                        Custom Selection
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setReportAccessMode('all')}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            reportAccessMode === 'all'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-700 text-gray-400 hover:text-gray-300'
                                        }`}
                                    >
                                        All Reports (Unrestricted)
                                    </button>
                                </div>

                                {reportAccessMode === 'custom' && (
                                    <div className="space-y-2">
                                        {selectedReports.length === 0 && (
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                                                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                                                <span className="text-xs text-amber-300">No reports selected. Select at least one report for this broker.</span>
                                            </div>
                                        )}
                                        <div className="flex gap-2 mb-2">
                                            <button type="button" onClick={() => setSelectedReports(REPORT_OPTIONS.map(r => r.slug))} className="text-xs text-blue-400 hover:text-blue-300">Select All</button>
                                            <button type="button" onClick={() => setSelectedReports([])} className="text-xs text-gray-400 hover:text-gray-300">Deselect All</button>
                                        </div>
                                        <div className="max-h-48 overflow-y-auto space-y-1">
                                            {REPORT_OPTIONS.map(({ slug, label }) => (
                                                <label key={slug} className="flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedReports.includes(slug)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedReports(prev => [...prev, slug]);
                                                            } else {
                                                                setSelectedReports(prev => prev.filter(s => s !== slug));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-300">{label}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {reportAccessMode === 'all' && (
                                    <p className="text-xs text-gray-400 italic">This broker will have unrestricted access to all reports.</p>
                                )}
                            </div>

                            {/* Loan Book (Template) Access Control */}
                            <div className="border border-gray-600 rounded-lg p-4">
                                <div className="flex items-start gap-3 mb-4">
                                    <BookOpen className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-white">Loan Book Access</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Control which loan books this broker can view. Defaults to the primary loan book.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-3 mb-3">
                                    <button
                                        type="button"
                                        onClick={() => setTemplateAccessMode('custom')}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            templateAccessMode === 'custom'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-700 text-gray-400 hover:text-gray-300'
                                        }`}
                                    >
                                        Custom Selection
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setTemplateAccessMode('all')}
                                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            templateAccessMode === 'all'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-700 text-gray-400 hover:text-gray-300'
                                        }`}
                                    >
                                        All Loan Books (Unrestricted)
                                    </button>
                                </div>

                                {templateAccessMode === 'custom' && (
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {[...loanBooks].sort((a, b) => a.name.localeCompare(b.name)).map((lb) => {
                                            // For legacy mode, loan_book.id === fileTemplate.id so primaryTemplateId matches directly.
                                            // For unified mode, primaryTemplateId is the shared template and won't match any loan_book
                                            // unless an admin deliberately aligned it, so the badge just doesn't render.
                                            const isPrimary = lb.id === companyData?.primaryTemplateId;
                                            return (
                                                <label key={lb.id} className="flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedLoanBookIds.includes(lb.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedLoanBookIds(prev => [...prev, lb.id]);
                                                            } else {
                                                                setSelectedLoanBookIds(prev => prev.filter(id => id !== lb.id));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-300">
                                                        {lb.name}
                                                        {isPrimary && <span className="ml-2 text-xs text-blue-400">(Primary)</span>}
                                                        {lb.isAcquiredLoanBook && <span className="ml-2 text-xs text-amber-400">(Acquired)</span>}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                        {loanBooks.length === 0 && (
                                            <p className="text-sm text-gray-400">No loan books configured.</p>
                                        )}
                                    </div>
                                )}

                                {templateAccessMode === 'all' && (
                                    <p className="text-xs text-gray-400 italic">This broker will have unrestricted access to all loan books.</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="invite-notes" className="block text-sm font-medium text-gray-300 mb-2">
                                    Notes (Optional)
                                </label>
                                <input
                                    id="invite-notes"
                                    type="text"
                                    value={inviteNotes}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInviteNotes(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-3"
                                    placeholder="e.g., New hire, starts Monday"
                                />
                            </div>

                            {/* Broker-family: optional multi-broker linkage. super_admin/administrator only. */}
                            {isBrokerFamilyRole && canConfigureAnalystLinkage && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Linked Brokers (Optional)
                                    </label>
                                    <p className="text-xs text-gray-400 mb-2">
                                        This user will see the selected brokers' books (loans, clients, portfolio) in addition to their own. They earn no upfront or trail commission on a linked book. Leave empty for own-data-only.
                                    </p>
                                    <div className="bg-gray-700 rounded-md p-3 max-h-48 overflow-y-auto space-y-1">
                                        {brokerDocs
                                            .filter((b) => !b.mergedInto && b.status !== 'inactive')
                                            .sort((a, b) => a.currentName.localeCompare(b.currentName))
                                            .map((broker) => (
                                                <label key={broker.id} className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-600">
                                                    <input
                                                        type="checkbox"
                                                        checked={inviteLinkedBrokerDocIds.includes(broker.id)}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setInviteLinkedBrokerDocIds((prev) => [...prev, broker.id]);
                                                            } else {
                                                                setInviteLinkedBrokerDocIds((prev) => prev.filter((id) => id !== broker.id));
                                                            }
                                                        }}
                                                        className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                                    />
                                                    <span className="ml-2 text-sm text-gray-300">{broker.currentName}</span>
                                                </label>
                                            ))}
                                        {brokerDocs.length === 0 && (
                                            <p className="text-sm text-gray-400">No brokers configured yet.</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {(isBrokerFamilyRole || inviteRole === 'office_admin' || inviteRole === 'administrator' || inviteRole === 'marketing') && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                {isBrokerFamilyRole ? 'Broker Office (auto-filled from selection)' : 'Access Scope (Select Offices)'}
                            </label>
                            <div className="bg-gray-700 rounded-md p-4 space-y-3">
                                {/* Hide "Assign All Offices" for office_admin, broker family, and marketing roles */}
                                {userData?.role !== 'office_admin' && !isBrokerFamilyRole && inviteRole !== 'marketing' && (
                                    <label className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-600">
                                        <input type="checkbox" checked={assignAllOffices} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAssignAllOffices(e.target.checked)} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" />
                                        <span className="ml-3 text-sm font-semibold text-white">Assign All Offices</span>
                                    </label>
                                )}
                                <div className={`max-h-48 overflow-y-auto space-y-2 transition-opacity ${assignAllOffices ? 'opacity-50' : ''}`}>
                                    {availableOffices.length > 0 ? availableOffices.map((office: any) => (
                                        <label key={office.id} className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-600">
                                            <input type="checkbox" disabled={assignAllOffices} checked={assignAllOffices || inviteAccessScope.includes(office.id)} onChange={() => handleScopeChange(office.id)} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" />
                                            <span className="ml-3 text-sm text-white">{office.name}</span>
                                        </label>
                                    )) : <p className="text-sm text-gray-400">No offices found.</p>}
                                </div>
                            </div>
                        </div>
                    )}

                    {inviteMessage.text && (<div className={`p-3 rounded-md text-sm ${inviteMessage.type === 'success' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>{inviteMessage.text}</div>)}

                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={callerIsBrokerFiltered || isSubmitting} title={callerIsBrokerFiltered ? BROKER_FILTERED_ADMIN_COPY : undefined} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-600">{isSubmitting ? 'Sending...' : 'Send Invitation'}</button>
                    </div>
                </form>
            </Modal>

            {/* Edit Permissions Modal */}
            <Modal
                show={!!editPermissionsUser}
                onClose={() => setEditPermissionsUser(null)}
                title={`Edit Permissions — ${editPermissionsUser?.displayName || ''}`}
            >
                <div className="space-y-6">
                    {/* Report Access Control */}
                    <div className="border border-gray-600 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-300 mb-3">Report Access</label>
                        <div className="flex gap-4 mb-3">
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={editReportAccessMode === 'all'} onChange={() => setEditReportAccessMode('all')} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500" />
                                <span className="ml-2 text-sm text-gray-300">All Reports</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={editReportAccessMode === 'custom'} onChange={() => setEditReportAccessMode('custom')} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500" />
                                <span className="ml-2 text-sm text-gray-300">Custom</span>
                            </label>
                        </div>
                        {editReportAccessMode === 'custom' && (
                            <div className="space-y-2">
                                <div className="flex gap-2 mb-2">
                                    <button type="button" onClick={() => setEditSelectedReports(REPORT_OPTIONS.map(r => r.slug))} className="text-xs text-blue-400 hover:text-blue-300">Select All</button>
                                    <button type="button" onClick={() => setEditSelectedReports([])} className="text-xs text-gray-400 hover:text-gray-300">Deselect All</button>
                                </div>
                                <div className="max-h-48 overflow-y-auto space-y-1">
                                    {REPORT_OPTIONS.map(({ slug, label }) => (
                                        <label key={slug} className="flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={editSelectedReports.includes(slug)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setEditSelectedReports(prev => [...prev, slug]);
                                                    } else {
                                                        setEditSelectedReports(prev => prev.filter(s => s !== slug));
                                                    }
                                                }}
                                                className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Loan Book (Template) Access Control */}
                    <div className="border border-gray-600 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-300 mb-3">Loan Book Access</label>
                        <div className="flex gap-4 mb-3">
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={editTemplateAccessMode === 'all'} onChange={() => setEditTemplateAccessMode('all')} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500" />
                                <span className="ml-2 text-sm text-gray-300">All Loan Books</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <input type="radio" checked={editTemplateAccessMode === 'custom'} onChange={() => setEditTemplateAccessMode('custom')} className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500" />
                                <span className="ml-2 text-sm text-gray-300">Custom</span>
                            </label>
                        </div>
                        {editTemplateAccessMode === 'custom' && (
                            <div className="max-h-48 overflow-y-auto space-y-1">
                                {[...loanBooks].sort((a, b) => a.name.localeCompare(b.name)).map((lb) => (
                                    <label key={lb.id} className="flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-600">
                                        <input
                                            type="checkbox"
                                            checked={editSelectedLoanBookIds.includes(lb.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setEditSelectedLoanBookIds(prev => [...prev, lb.id]);
                                                } else {
                                                    setEditSelectedLoanBookIds(prev => prev.filter(id => id !== lb.id));
                                                }
                                            }}
                                            className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-300">
                                            {lb.name}
                                            {lb.isAcquiredLoanBook && <span className="ml-2 text-xs text-amber-400">(Acquired)</span>}
                                        </span>
                                    </label>
                                ))}
                                {loanBooks.length === 0 && (
                                    <p className="text-sm text-gray-400">No loan books configured.</p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Broker-family multi-broker linkage editor. super_admin/administrator only.
                        Links this one login to many brokers' books (loans, clients, portfolio).
                        The user earns no commission on a linked book; only their own
                        attributed clients appear on their commission settings page. */}
                    {(editPermissionsUser?.role === 'broker' || editPermissionsUser?.role === 'broker_assistant' || editPermissionsUser?.role === 'credit_analyst') && canConfigureAnalystLinkage && (
                        <div className="border border-gray-600 rounded-lg p-4">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Linked Brokers</label>
                            <p className="text-xs text-gray-400 mb-3">
                                This user sees the selected brokers' books (loans, clients, portfolio) in addition to their own. They earn no upfront or trail commission on a linked book; only their own attributed clients appear on their commission settings. Leave empty for own-data-only.
                            </p>
                            <div className="flex gap-2 mb-2">
                                <button type="button" onClick={() => setEditLinkedBrokerDocIds(brokerDocs.filter((b) => !b.mergedInto && b.status !== 'inactive').map((b) => b.id))} className="text-xs text-blue-400 hover:text-blue-300">Select All</button>
                                <button type="button" onClick={() => setEditLinkedBrokerDocIds([])} className="text-xs text-gray-400 hover:text-gray-300">Deselect All</button>
                            </div>
                            <div className="bg-gray-700 rounded-md p-3 max-h-48 overflow-y-auto space-y-1">
                                {brokerDocs
                                    .filter((b) => !b.mergedInto && b.status !== 'inactive')
                                    .sort((a, b) => a.currentName.localeCompare(b.currentName))
                                    .map((broker) => (
                                        <label key={broker.id} className="flex items-center cursor-pointer p-2 rounded hover:bg-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={editLinkedBrokerDocIds.includes(broker.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setEditLinkedBrokerDocIds((prev) => [...prev, broker.id]);
                                                    } else {
                                                        setEditLinkedBrokerDocIds((prev) => prev.filter((id) => id !== broker.id));
                                                    }
                                                }}
                                                className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">{broker.currentName}</span>
                                        </label>
                                    ))}
                                {brokerDocs.length === 0 && (
                                    <p className="text-sm text-gray-400">No brokers configured yet.</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Per-(broker, loan book) view grants. Finer than Linked Brokers above:
                        links this login to ONE loan book of ONE broker (one of their books,
                        not their whole portfolio). View-only, no commission. */}
                    {(editPermissionsUser?.role === 'broker' || editPermissionsUser?.role === 'broker_assistant' || editPermissionsUser?.role === 'credit_analyst') && canConfigureAnalystLinkage && (
                        <div className="border border-gray-600 rounded-lg p-4">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Linked Loan Books (per broker)</label>
                            <p className="text-xs text-gray-400 mb-3">
                                Grant access to a SPECIFIC loan book of a specific broker (one of their loan books only, not their whole portfolio). The user sees those clients and loans as view rows with no commission. Add one row per (broker, loan book) pair.
                            </p>
                            <div className="space-y-2">
                                {editLinkedLoanBookGrants.map((grant, idx) => (
                                    <div key={idx} className="flex gap-2 items-center">
                                        <select
                                            value={grant.brokerDocId}
                                            onChange={(e) => setEditLinkedLoanBookGrants((prev) => prev.map((g, i) => (i === idx ? { ...g, brokerDocId: e.target.value } : g)))}
                                            className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm"
                                        >
                                            <option value="">Select broker...</option>
                                            {brokerDocs
                                                .filter((b) => !b.mergedInto && b.status !== 'inactive')
                                                .sort((a, b) => a.currentName.localeCompare(b.currentName))
                                                .map((b) => (<option key={b.id} value={b.id}>{b.currentName}</option>))}
                                        </select>
                                        <select
                                            value={grant.loanBookId}
                                            onChange={(e) => setEditLinkedLoanBookGrants((prev) => prev.map((g, i) => (i === idx ? { ...g, loanBookId: e.target.value } : g)))}
                                            className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm"
                                        >
                                            <option value="">Select loan book...</option>
                                            {loanBooks
                                                .filter((lb) => lb.isEnabled)
                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                .map((lb) => (<option key={lb.id} value={lb.id}>{lb.name}</option>))}
                                        </select>
                                        <button
                                            type="button"
                                            onClick={() => setEditLinkedLoanBookGrants((prev) => prev.filter((_, i) => i !== idx))}
                                            className="text-xs text-gray-400 hover:text-red-400 px-2"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                                {editLinkedLoanBookGrants.length === 0 && (
                                    <p className="text-sm text-gray-400">No per-book grants. The user sees only their own data, plus any whole brokers linked above.</p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setEditLinkedLoanBookGrants((prev) => [...prev, { brokerDocId: '', loanBookId: '' }])}
                                className="mt-2 text-xs text-blue-400 hover:text-blue-300"
                            >
                                + Add loan-book grant
                            </button>
                        </div>
                    )}

                    {/* "Also a broker" owner-operator link — administrator/super_admin
                        targets only (the brokerage principal who also writes loans).
                        Sets user.brokerDocId so the Client Care digests route to them.
                        Does NOT change their data scope (privileged by role). */}
                    {(editPermissionsUser?.role === 'administrator' || editPermissionsUser?.role === 'super_admin') && canConfigureAnalystLinkage && (
                        <div className="border border-gray-600 rounded-lg p-4">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Also a Broker (owner-operator)</label>
                            <p className="text-xs text-gray-400 mb-3">
                                If this {editPermissionsUser?.role === 'super_admin' ? 'super admin' : 'administrator'} also writes loans, link them to their broker so they receive their own discharge, arrears and clawback email digests. This does not change their data access.
                            </p>
                            <div className="flex gap-2">
                                <select
                                    value={editBrokerIdentityDocId}
                                    onChange={(e) => setEditBrokerIdentityDocId(e.target.value)}
                                    className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-md p-2 text-sm"
                                >
                                    <option value="">— Not a broker —</option>
                                    {brokerDocs
                                        .filter((b) => !b.mergedInto && b.status !== 'inactive')
                                        .sort((a, b) => a.currentName.localeCompare(b.currentName))
                                        .map((broker) => (
                                            <option key={broker.id} value={broker.id}>{broker.currentName}</option>
                                        ))}
                                </select>
                                <button
                                    type="button"
                                    onClick={handleSaveBrokerIdentity}
                                    disabled={savingBrokerIdentity || editBrokerIdentityDocId === (editPermissionsUser?.brokerDocId ?? '')}
                                    className="px-3 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed whitespace-nowrap"
                                >
                                    {savingBrokerIdentity ? 'Saving...' : 'Save link'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Lead Capture admin-config — only when subscriber has the
                        lead_capture add-on. Two sub-sections:
                          1. Visibility (allowedLeadPages) — applies to broker-family targets;
                             admin-tier roles bypass the filter and always see all tabs.
                          2. Availability override — only for broker / broker_assistant /
                             credit_analyst targets, lets the admin flip routing-side fields
                             on the broker's behalf without the broker logging in. */}
                    {hasLeadCapture && (
                        <div className="border border-gray-600 rounded-lg p-4 bg-gray-800/60">
                            <div className="flex items-center gap-2 mb-3">
                                <Inbox className="w-4 h-4 text-blue-400" />
                                <label className="block text-sm font-medium text-gray-300">Lead Capture</label>
                            </div>

                            {/* Visibility — allowed Lead Capture pages */}
                            <div className="mb-4">
                                <p className="text-xs text-gray-400 mb-2">
                                    Allowed Lead Capture Pages
                                    <span className="ml-2 text-[0.7rem] text-gray-500">
                                        (Admin-tier roles always see all tabs — this filter only applies to brokers, broker assistants, and credit analysts.)
                                    </span>
                                </p>
                                <div className="space-y-1">
                                    {/* Baseline tabs — always allowed, shown disabled+checked. */}
                                    <label className="flex items-center p-1.5 rounded opacity-70 cursor-not-allowed">
                                        <input type="checkbox" checked disabled className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" />
                                        <span className="ml-2 text-sm text-gray-300">Lead Inbox <span className="text-[0.7rem] text-gray-500">(always allowed)</span></span>
                                    </label>
                                    <label className="flex items-center p-1.5 rounded opacity-70 cursor-not-allowed">
                                        <input type="checkbox" checked disabled className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded" />
                                        <span className="ml-2 text-sm text-gray-300">My Availability <span className="text-[0.7rem] text-gray-500">(always allowed)</span></span>
                                    </label>
                                    {/* Toggle-able pages */}
                                    {LEAD_PAGE_OPTIONS.map(({ slug, label }) => (
                                        <label key={slug} className="flex items-center cursor-pointer p-1.5 rounded hover:bg-gray-600">
                                            <input
                                                type="checkbox"
                                                checked={editAllowedLeadPages.includes(slug)}
                                                onChange={(e) => {
                                                    setEditLeadFieldsDirty((d) => ({ ...d, pages: true }));
                                                    if (e.target.checked) {
                                                        setEditAllowedLeadPages((prev) => [...prev, slug]);
                                                    } else {
                                                        setEditAllowedLeadPages((prev) => prev.filter((s) => s !== slug));
                                                    }
                                                }}
                                                className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                            />
                                            <span className="ml-2 text-sm text-gray-300">{label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Availability override — broker-family only */}
                            {AVAILABILITY_OVERRIDE_ROLES.has(editPermissionsUser?.role) && (
                                <div className="border-t border-gray-700 pt-4 mt-4 space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-amber-400" />
                                        <p className="text-xs text-gray-300 font-medium">
                                            Availability Override
                                        </p>
                                        <span className="text-[0.7rem] text-gray-500">
                                            (changes write directly to the broker's availability — they don't need to log in.)
                                        </span>
                                    </div>

                                    {/* Accepting New Leads */}
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <span className="text-sm text-gray-300">Accepting New Leads</span>
                                        <input
                                            type="checkbox"
                                            checked={editAcceptingNewLeads}
                                            onChange={(e) => {
                                                setEditAcceptingNewLeads(e.target.checked);
                                                setEditLeadFieldsDirty((d) => ({ ...d, accepting: true }));
                                            }}
                                            className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                        />
                                    </label>

                                    {/* Max Open Leads */}
                                    <div className="flex items-center justify-between">
                                        <label htmlFor="edit-max-open-leads" className="text-sm text-gray-300">
                                            Max Open Leads
                                            <span className="ml-1 text-[0.7rem] text-gray-500">(0 = no cap)</span>
                                        </label>
                                        <input
                                            id="edit-max-open-leads"
                                            type="number"
                                            min={0}
                                            max={500}
                                            value={editMaxOpenLeads ?? ''}
                                            onChange={(e) => {
                                                const v = e.target.value === '' ? undefined : Math.max(0, Math.min(500, parseInt(e.target.value, 10) || 0));
                                                setEditMaxOpenLeads(v);
                                                setEditLeadFieldsDirty((d) => ({ ...d, maxOpen: true }));
                                            }}
                                            className="w-24 px-2 py-1 text-sm rounded bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:outline-none"
                                            placeholder="Default"
                                        />
                                    </div>

                                    {/* Weekly schedule (read-only preview) */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <CalendarDays className="w-4 h-4 text-gray-400" />
                                            <p className="text-xs text-gray-400">Weekly Schedule (read-only)</p>
                                        </div>
                                        {editLeadWeeklySchedule ? (
                                            <div className="grid grid-cols-7 gap-1 text-[0.7rem]">
                                                {WEEKDAY_LABELS.map(({ key, label }) => {
                                                    const windows = (editLeadWeeklySchedule[key] || []) as Array<{ start: string; end: string }>;
                                                    const hasAny = windows.length > 0;
                                                    return (
                                                        <div
                                                            key={key}
                                                            className={`rounded px-1.5 py-1 text-center border ${hasAny ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-gray-700 bg-gray-800 text-gray-500'}`}
                                                            title={hasAny ? windows.map((w) => `${w.start}–${w.end}`).join(', ') : 'Off'}
                                                        >
                                                            <div className="font-semibold text-gray-200">{label}</div>
                                                            <div>{hasAny ? `${windows.length}×` : '—'}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-xs text-gray-500 italic">No schedule configured. Broker uses defaults.</p>
                                        )}
                                        <a
                                            href={`/leads/availability?broker=${editPermissionsUser?.id || ''}`}
                                            className="mt-2 inline-block text-xs text-blue-400 hover:text-blue-300 underline"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setEditPermissionsUser(null);
                                                window.location.href = `/leads/availability?broker=${editPermissionsUser?.id || ''}`;
                                            }}
                                        >
                                            Edit weekly schedule for this broker →
                                        </a>
                                    </div>

                                    {/* Out of Office override */}
                                    <div className="space-y-2">
                                        <label className="flex items-center justify-between cursor-pointer">
                                            <span className="text-sm text-gray-300">Out of Office</span>
                                            <input
                                                type="checkbox"
                                                checked={editLeadOutOfOfficeEnabled}
                                                onChange={(e) => {
                                                    setEditLeadOutOfOfficeEnabled(e.target.checked);
                                                    setEditLeadFieldsDirty((d) => ({ ...d, ooo: true }));
                                                }}
                                                className="w-4 h-4 text-blue-600 bg-gray-600 border-gray-500 rounded"
                                            />
                                        </label>
                                        {editLeadOutOfOfficeEnabled && (
                                            <div className="flex items-center justify-between">
                                                <label htmlFor="edit-ooo-until" className="text-sm text-gray-300">
                                                    Out of office until
                                                </label>
                                                <input
                                                    id="edit-ooo-until"
                                                    type="date"
                                                    value={editLeadOutOfOfficeUntil}
                                                    onChange={(e) => {
                                                        setEditLeadOutOfOfficeUntil(e.target.value);
                                                        setEditLeadFieldsDirty((d) => ({ ...d, ooo: true }));
                                                    }}
                                                    className="px-2 py-1 text-sm rounded bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Link to Commission Settings */}
                    <div className="pt-2 border-t border-gray-700">
                        <p className="text-xs text-gray-500 mb-2">For commission split percentages and base salary settings:</p>
                        <a
                            href={`/broker-commission-settings?broker=${editPermissionsUser?.brokerDocId || ''}`}
                            className="text-sm text-blue-400 hover:text-blue-300 underline"
                            onClick={(e) => {
                                e.preventDefault();
                                setEditPermissionsUser(null);
                                window.location.href = `/broker-commission-settings?broker=${editPermissionsUser?.brokerDocId || ''}`;
                            }}
                        >
                            Open Commission Settings →
                        </a>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={() => setEditPermissionsUser(null)}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSavePermissions}
                            disabled={savingPermissions}
                            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                        >
                            {savingPermissions ? 'Saving...' : 'Save Permissions'}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmLabel={confirmDialog.confirmLabel}
                confirmVariant={confirmDialog.variant}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
            />
        </div>
    );
};

export default TeamManagementPage;